import { useCallback, useEffect, useRef } from 'react';
import {
  Dimensions,
  Keyboard,
  Platform,
  StatusBar,
  TextInput,
} from 'react-native';
import type { FlashListRef } from '@shopify/flash-list';
import { v } from '../utils/libs';

/** Breathing room kept between the focused input and the keyboard. */
const KEYBOARD_GAP = v(16);
/**
 * Android reports the two numbers we compare in different origins:
 *
 *  - `measureInWindow` is relative to the root view's viewport. Fabric adds
 *    `RootViewUtil.getViewportOffset()`, which is `rootView.locationInWindow.y
 *    - windowVisibleDisplayFrame.top` — i.e. minus the status bar height for a
 *    root view that draws edge to edge.
 *  - `keyboardDidShow`'s `endCoordinates.screenY` is `windowVisibleDisplayFrame
 *    .bottom`, a raw screen coordinate that still counts the status bar.
 *
 * Comparing them directly loses exactly one status bar height, so the scroll
 * falls short by that much and the keyboard keeps covering the input. Rebase
 * the keyboard onto the measurement origin instead of padding the gap.
 */
const MEASURE_ORIGIN_OFFSET =
  Platform.OS === 'android' ? (StatusBar.currentHeight ?? 0) : 0;
/**
 * How far the list may drift from the offset we scrolled it to before we
 * treat the position as the user's own and skip restoring it.
 */
const USER_SCROLL_TOLERANCE = v(24);

interface KeyboardAwareListTarget {
  /** Identifies the list so switching tabs cancels a pending restore. */
  key: string;
  listRef: { current: FlashListRef<any> | null };
  /** Live scroll offset, written by the list's own onScroll handler. */
  offsetRef: { current: number };
}

type InputRect = { y: number; height: number } | null;

const measureFocusedInput = (onMeasured: (rect: InputRect) => void) => {
  if (Platform.OS === 'web') {
    const element =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    if (!element?.getBoundingClientRect) return onMeasured(null);
    const rect = element.getBoundingClientRect();
    return onMeasured({ y: rect.top, height: rect.height });
  }
  const input = TextInput.State.currentlyFocusedInput() as any;
  if (!input?.measureInWindow) return onMeasured(null);
  input.measureInWindow((_x: number, y: number, _w: number, height: number) =>
    onMeasured(Number.isFinite(y) ? { y, height } : null),
  );
};

/**
 * Keeps a focused list row above the on-screen keyboard.
 *
 * Scrolls by the exact overlap and only when the focused input is actually
 * covered — a row that is already visible is left alone — then puts the list
 * back where it was once the keyboard hides (unless the user scrolled in the
 * meantime, or switched to another list).
 *
 * `getTarget` must be identity-stable and resolve the currently rendered
 * list at call time.
 */
export default function useKeyboardAwareListScroll(
  getTarget: () => KeyboardAwareListTarget,
) {
  /** The offset to go back to once the keyboard hides. */
  const pendingRef = useRef<{
    key: string;
    from: number;
    to: number | null;
  } | null>(null);
  const keyboardTopRef = useRef(0);
  /** Guards against reacting to inputs outside the list (e.g. the search bar). */
  const rowFocusedRef = useRef(false);

  const scrollAboveKeyboard = useCallback(
    (keyboardTop: number) => {
      if (!rowFocusedRef.current || keyboardTop <= 0) return;
      measureFocusedInput(rect => {
        if (!rect) return;
        const overlap = rect.y + rect.height + KEYBOARD_GAP - keyboardTop;
        if (overlap <= 1) return;
        const { key, listRef, offsetRef } = getTarget();
        const target = Math.max(0, offsetRef.current + overlap);
        if (!pendingRef.current || pendingRef.current.key !== key) {
          pendingRef.current = { key, from: offsetRef.current, to: null };
        }
        pendingRef.current.to = target;
        listRef.current?.scrollToOffset({ offset: target, animated: true });
      });
    },
    [getTarget],
  );

  const restorePosition = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || pending.to === null) return;
    const { key, listRef, offsetRef } = getTarget();
    if (key !== pending.key) return;
    if (Math.abs(offsetRef.current - pending.to) > USER_SCROLL_TOLERANCE) return;
    listRef.current?.scrollToOffset({ offset: pending.from, animated: true });
  }, [getTarget]);

  /** Pass to every input inside the list. */
  const onInputFocus = useCallback(() => {
    rowFocusedRef.current = true;
    if (keyboardTopRef.current > 0) {
      // Keyboard is already up (moving between rows): adjust now, but keep the
      // anchor taken before it appeared so the restore lands where we started.
      scrollAboveKeyboard(keyboardTopRef.current);
      return;
    }
    const { key, offsetRef } = getTarget();
    pendingRef.current = { key, from: offsetRef.current, to: null };
  }, [getTarget, scrollAboveKeyboard]);

  /** Pass to the list — a user drag cancels the pending restore. */
  const onScrollBeginDrag = useCallback(() => {
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      // react-native-web's Keyboard is a stub; the visual viewport shrinking
      // is the only signal a soft keyboard is open (hardware ones never fire).
      const viewport = (globalThis as any).visualViewport;
      if (!viewport) return;
      const onViewportResize = () => {
        const visibleBottom = viewport.height + viewport.offsetTop;
        const covered = window.innerHeight - visibleBottom;
        if (covered > 120) {
          keyboardTopRef.current = visibleBottom;
          scrollAboveKeyboard(visibleBottom);
        } else if (keyboardTopRef.current > 0) {
          keyboardTopRef.current = 0;
          rowFocusedRef.current = false;
          restorePosition();
        }
      };
      viewport.addEventListener('resize', onViewportResize);
      return () => viewport.removeEventListener('resize', onViewportResize);
    }

    // didShow (not willShow) so the measurement already includes any inset
    // adjustment iOS made for the keyboard — otherwise we scroll twice.
    const showSub = Keyboard.addListener('keyboardDidShow', event => {
      const { screenY, height } = event.endCoordinates;
      const keyboardTop =
        (screenY || Dimensions.get('screen').height - (height ?? 0)) -
        MEASURE_ORIGIN_OFFSET;
      keyboardTopRef.current = keyboardTop;
      scrollAboveKeyboard(keyboardTop);
    });
    // willHide on iOS: the keyboard inset is still applied at that point, so
    // the restore animates alongside the dismissal instead of fighting the
    // offset clamp iOS performs once the inset is removed.
    const hideSub = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        keyboardTopRef.current = 0;
        rowFocusedRef.current = false;
        restorePosition();
      },
    );
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [scrollAboveKeyboard, restorePosition]);

  return { onInputFocus, onScrollBeginDrag };
}
